/**
 * 리포트 보관함 종류 레지스트리 SSoT(065 FR-008·FR-009).
 *
 * 서버 `ReportKind` enum 과 키 문자열이 같아야 한다 — 종류 추가 = 서버 enum 1행 + 이 파일 1행.
 * 페이지·필터·최신 스트립·GNB(workspaceNav)·breadcrumb 이 전부 이 파일을 참조한다 — 라벨·상세 route·
 * 빈 상태 문구·유도 route 를 다른 곳에 재기입하지 않는다.
 *
 * `ArchiveKind`·`ARCHIVE_KINDS`·`archiveKindOf` 는 서버 `ReportKind.displayKind()` 의 미러다 — 현재
 * 접힘(원천 ≠ 표시) 종류가 없어 항등이지만, 확장점으로 형태를 유지한다. 노출 보류 종류는 `visibility.ts` 가
 * 소유한다(서버 `ReportKind.hidden` 미러).
 *
 * ⚠️ 도메인 데이터라 config/ 가 아닌 data/analysis/ 에 둔다(workspaceNav 와 같은 이유).
 */
import type { LucideIcon } from 'lucide-vue-next';
import { Archive, ClipboardList, LayoutGrid } from 'lucide-vue-next';

import { isArchiveKindVisible } from './visibility';

/** 서버 `ReportKind` 와 동일 문자열. `REPORT_REGISTRY` 키. */
export type ReportKind = 'ASSESSMENT' | 'DIAGNOSIS' | 'SUSI_CARDS';

/** 보관함 표시 종류 — 서버 `ReportKind.displayKind()` 치역(현재 원천과 동일). 필터·스트립·GNB·`?kind=` 단위. */
export type ArchiveKind = ReportKind;

export interface ReportKindMeta {
  /** 종류 표시명(필터 칩·GNB 항목·breadcrumb 페이지 라벨). */
  label: string;
  /** GNB 하위 항목 보조 설명. */
  desc: string;
  /** 행·스트립 배지 아이콘. */
  icon: LucideIcon;
  /** 원천 PK → 상세 route(contracts §3). */
  detailRoute: (id: number) => string;
  /** 종류 필터 0건 제목. */
  emptyTitle: string;
  /** 종류 필터 0건 설명. */
  emptyDescription: string;
  /** 0건 유도 버튼 라벨. */
  emptyCtaLabel: string;
  /** 0건 유도 목적지(해당 축 화면). */
  emptyCtaRoute: string;
  /** 최신 스트립 카드 상단 라벨. */
  latestLabel: string;
}

/** 원천 종류 전부(서버 선언 순) — 레지스트리 키 검증(`isReportKind`)용. 화면 순회는 `ARCHIVE_KINDS` 를 쓴다. */
const REPORT_KINDS: readonly ReportKind[] = ['ASSESSMENT', 'DIAGNOSIS', 'SUSI_CARDS'] as const;

/** 표시 종류 전부(서버 `displayKind()` 치역, 선언 순) — 숨김 적용 전. */
const ARCHIVE_KINDS_ALL: readonly ArchiveKind[] = REPORT_KINDS;

/**
 * 화면에 노출하는 표시 종류(서버 선언 순) — 필터 칩·최신 스트립·GNB 순회·`?kind=` 검증 단위(072).
 * `visibility.ts` 의 숨김 종류는 여기서 빠진다(서버 `ReportKind.hidden` 미러) — 상세 route 는 `REPORT_REGISTRY` 에 남는다.
 */
export const ARCHIVE_KINDS: readonly ArchiveKind[] = ARCHIVE_KINDS_ALL.filter(isArchiveKindVisible);

export const REPORT_REGISTRY: Record<ReportKind, ReportKindMeta> = {
  ASSESSMENT: {
    label: '검사 결과',
    desc: '진로·학습·심리·진학 검사 결과를 다시 봅니다.',
    icon: Archive,
    detailRoute: (id) => `/results/${id}`,
    emptyTitle: '아직 완료한 검사가 없어요',
    emptyDescription: '진로심리검사에서 나를 알아가는 검사를 시작해 보세요.',
    emptyCtaLabel: '진로심리검사로 이동',
    emptyCtaRoute: '/tests',
    latestLabel: '최신 검사 결과',
  },
  DIAGNOSIS: {
    label: '성적 분석 결과',
    desc: '저장한 정시·수시 분석 결과를 다시 봅니다.',
    icon: ClipboardList,
    detailRoute: (id) => `/diagnosis/${id}`,
    emptyTitle: '아직 저장한 성적 분석 결과가 없어요',
    emptyDescription:
      '정시·수시 분석에서 학과나 전형을 열고 [분석 결과 저장]을 누르면 그 대상의 분석 결과가 저장됩니다.',
    emptyCtaLabel: '수시 분석으로 이동',
    emptyCtaRoute: '/susi',
    latestLabel: '최신 성적 분석 결과',
  },
  SUSI_CARDS: {
    label: '수시 6장 전략',
    desc: '생성한 6장 카드 리포트를 다시 봅니다.',
    icon: LayoutGrid,
    detailRoute: (id) => `/susi/cards/${id}`,
    emptyTitle: '아직 만든 수시 6장 전략 리포트가 없어요',
    emptyDescription:
      '조건을 고르고 한 번 생성하면 여기에 쌓입니다. 조건을 바꿔 여러 번 만들어 비교해 보세요.',
    emptyCtaLabel: '수시 6장 전략 만들기',
    emptyCtaRoute: '/susi/cards',
    latestLabel: '최신 수시 6장 전략',
  },
};

/** 문자열이 등록된 원천 kind 인지 판정 — 레지스트리 키 검증용. `?kind=` 필터 검증은 `isArchiveKind`. */
export function isReportKind(value: unknown): value is ReportKind {
  return typeof value === 'string' && (REPORT_KINDS as readonly string[]).includes(value);
}

/** 문자열이 노출 표시 종류인지 판정 — 주소 쿼리(`?kind=`)·breadcrumb 라벨 검증용. */
export function isArchiveKind(value: unknown): value is ArchiveKind {
  return typeof value === 'string' && (ARCHIVE_KINDS as readonly string[]).includes(value);
}

/** 원천 종류 → 표시 종류(서버 `displayKind()` 미러). 현재 접힘 종류가 없어 항등. */
export function archiveKindOf(kind: ReportKind): ArchiveKind {
  return kind;
}

/** kind 필터 딥링크(GNB·각 축 화면의 "이력 보기"·breadcrumb 복귀 링크 SSoT) — 표시 종류만 받는다. */
export function reportArchiveRoute(kind?: ArchiveKind | null): string {
  return kind ? `/reports?kind=${kind}` : '/reports';
}
